import * as signalR from "@microsoft/signalr";
import type {
  AttachmentUploadGrantChangedDto,
  HistoryPage,
  MessageDto,
  VisitorJoinResult,
} from "./protocol/types.js";
import type { WidgetConfig } from "./config.js";
import type { WidgetStorage } from "./storage.js";
import { defaultBackoffOptions, jitteredDelayMs } from "./protocol/backoff.js";
import { SeenMessageIds } from "./protocol/dedup.js";
import { SequenceTracker } from "./protocol/sequence.js";
import { captureTrafficSource } from "./traffic.js";
import { guardAsync } from "./errors.js";

export type ConnectionState = "connecting" | "connected" | "reconnecting" | "disconnected";

/** Thrown by `sendMessage` when nothing was sent to the server at all - safe for a caller to retry
 * on its own (e.g. once the connection reports "connected" again). */
export class NotConnectedError extends Error {
  constructor() {
    super("Not connected.");
    this.name = "NotConnectedError";
  }
}

/**
 * Thrown when an invoke was actually in flight and the outcome is unknown. `VisitorHub.SendMessageAsync`
 * does dedup by `clientMessageId` server-side (`5-07`) - a same-id retry would be safe in principle -
 * but this widget does not yet build that retry path (`ui/widget.ts`'s send failure UI surfaces "not
 * sure it sent" rather than resending); the caller must decide instead of this class silently resending.
 *
 * `5-17`: what `ui/widget.ts` does do is keep the message's optimistic bubble paired to its
 * `clientMessageId` after this is thrown, so that if the send *had* landed, the server's own copy -
 * live, or in the history a resuming `JoinAsync` replays - resolves that bubble instead of rendering
 * the same message a second time. See that method's own branch for why the warning may only be
 * cleared by that arrival and by nothing else.
 */
export class SendOutcomeUnknownError extends Error {
  constructor(cause: unknown) {
    super("Send outcome unknown - the connection may have dropped mid-request.");
    this.name = "SendOutcomeUnknownError";
    this.cause = cause;
  }
}

/**
 * Wraps `@microsoft/signalr`'s `HubConnection` with exactly the behaviour realtime.md's Client
 * protocol section and the embeddable-widget skill require: resume by `lastKnownSequence`, strict
 * ordering by `sequence` (never arrival order), full-jitter reconnect backoff, and dedup of the
 * sender's own echoed-back message (Fan-out path). A hub method here is a thin wrapper, same rule
 * the server side follows - no protocol decisions live in the UI layer that calls this.
 */
export class VisitorConnection {
  private readonly connection: signalR.HubConnection;
  private readonly seenMessageIds = new SeenMessageIds();
  private messageListener: ((message: MessageDto) => void) | null = null;
  private stateListener: ((state: ConnectionState) => void) | null = null;
  private grantListener: ((hasAttachmentUploadGrant: boolean) => void) | null = null;
  private conversationId: string | null = null;
  private sequenceTracker = new SequenceTracker();

  /**
   * `accessTokenFactory` is a **factory**, not a token, and `@microsoft/signalr` calls it on every
   * negotiate - the first connect and every automatic-reconnect attempt alike. `5-17` noted that
   * this one closed over a captured `session.token`, and that it was harmless only because the
   * visitor token never rotated. `17-07` makes it rotate, so the capture became a live defect: a
   * connection opened days ago, dropped, and re-established would negotiate with the token this page
   * load started with - the one renewal had already replaced - and the reconnect would be rejected
   * by a server that is entirely right to reject it. The visitor would see "Reconnecting…" forever.
   *
   * So the token arrives as `tokenProvider`, asynchronous because `session.ts`'s own `token()` may
   * have to renew before it can answer. This is *the* moment renewal has to happen for the widget to
   * survive its own token's lifetime: SignalR is asking what to present, and the answer is computed
   * then rather than remembered from before.
   *
   * Mirrors `ago-console`'s `OperatorConnection`, which took the same shape for the same reason in
   * `5-16` - the two repositories keep converging on this because it is the same problem seen from
   * two token issuers.
   */
  constructor(
    private readonly config: WidgetConfig,
    tokenProvider: () => Promise<string>,
    private readonly storage: WidgetStorage,
  ) {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${config.apiBaseUrl}/hubs/visitor`, {
        accessTokenFactory: tokenProvider,
        // The widget never uses cookies (embeddable-widget skill: "No cookies on the host
        // domain") - identity travels entirely through the JWT above. @microsoft/signalr's own
        // default is `withCredentials: true`, which sends the browser's cookie jar for this
        // origin and then requires the server's CORS policy to answer with
        // `Access-Control-Allow-Credentials: true` for every allowed origin - found live
        // (5-09): the negotiate preflight failed with exactly that CORS error against the real
        // per-site CORS policy (`5-01`), which does not set that header, on purpose, since this
        // widget was never supposed to be sending credentials in the first place.
        withCredentials: false,
      })
      // `5-14`: without this, `@microsoft/signalr`'s default logger is `ConsoleLogger(Information)`,
      // and `WebSocketTransport` logs "WebSocket connected to {url}" at exactly `Information` - after
      // it has appended `access_token=` to that url. Confirmed live against a running API, not
      // assumed from the console's own case: the visitor's real JWT was printed in full on every
      // connect. `coding-style.md` bans it outright ("Never log message bodies, tokens, presigned
      // URLs..."), and a visitor token being a smaller blast radius than an operator one
      // (`api-design.md`: it grants only that visitor's own conversation) does not make it a
      // different rule. It is worse here in one respect the console does not share: this bundle runs
      // on a page the widget does not control, alongside whatever else that page loaded.
      //
      // `Warning` rather than `Error`/`None` because it is the *lowest* level that suppresses that
      // line while still surfacing the diagnostics worth having: HTTP request errors and timeouts,
      // the page-freeze warning that predicts a dropped connection, and an unhandled server->client
      // method name.
      //
      // Unconditional, with no build-mode branch. Two reasons. The token-bearing line sits at
      // `Information`, which is *above* `Debug` and `Trace` on this library's ladder, so every level
      // verbose enough to be worth switching to also prints the token - there is no dev setting that
      // is both more informative and token-free. And `build.mjs` has no dev/production mode to hang a
      // condition on in the first place (one esbuild invocation, always minified; `define` carries
      // only the version and the API base URL, and esbuild gives no `import.meta.env`), so a
      // conditional would mean inventing a build flag whose only effect is to reintroduce the leak.
      .configureLogging(signalR.LogLevel.Warning)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (context) =>
          jitteredDelayMs(context.previousRetryCount + 1, defaultBackoffOptions),
      })
      .build();

    this.connection.on("MessageReceived", (dto: MessageDto) => this.handleIncoming(dto));

    // `25-110`: the live push - an operator's grant/revoke click reaches this connection the instant
    // it happens, no reload, no reconnect required. A thin wrapper, like `MessageReceived` right
    // above: no filtering by `this.conversationId` (this widget only ever holds one conversation open
    // per connection, and the server only ever addresses this connection's own principal), no business
    // logic - just forward `granted` to whoever is listening.
    this.connection.on("AttachmentUploadGrantChanged", (dto: AttachmentUploadGrantChangedDto) => {
      this.grantListener?.(dto.granted);
    });

    // realtime.md: the server may ask a client to reconnect on its own schedule before a draining
    // node shuts down. Purely informational here - the drain sequence's own subsequent disconnect
    // is what actually triggers `onreconnecting`/`onreconnected` below; this hook exists so a host
    // page (or this widget's own UI) can log or display it, not to duplicate that flow.
    this.connection.on("Reconnect", (_hint: { after: string }) => {
      // Intentionally no-op beyond what onreconnecting/onreconnected already do - see doc comment.
    });

    this.connection.onreconnecting(() => this.stateListener?.("reconnecting"));
    this.connection.onreconnected(() => void this.resumeAfterReconnect());
    this.connection.onclose(() => this.stateListener?.("disconnected"));
  }

  onMessage(listener: (message: MessageDto) => void): void {
    this.messageListener = listener;
  }

  onStateChange(listener: (state: ConnectionState) => void): void {
    this.stateListener = listener;
  }

  /**
   * `25-110`: fires two ways now, and both matter.
   *
   * **Live**, the instant an operator's own grant/revoke click lands - the `"AttachmentUploadGrantChanged"`
   * listener above calls this connection's `grantListener` directly, with no reconnect and no reload,
   * for a visitor who has been connected the whole time. This is the fix for the gap this doc comment
   * used to describe in as many words: "there still is none... a visitor who stays connected the whole
   * time still does not [see it], until... the page is reloaded."
   *
   * **Riding a rejoin**, exactly as before `25-110` - fired with the fresh
   * `VisitorJoinResult.hasAttachmentUploadGrant` on the initial `start()` and on every automatic
   * `resumeAfterReconnect()` below. This is not merely legacy: it is the backstop for a live push that
   * a drop-and-recover window could have missed entirely (`onreconnected` re-running `JoinAsync` is
   * the same self-healing read this widget already relied on for every other kind of missed update),
   * so a visitor whose connection drops and recovers, or whose laptop sleeps and wakes, still catches
   * up correctly even if the live push above never arrived. A missing field (an older server) folds to
   * `false` at the one place both call sites read it, `emitGrantChange` below - the closed-by-default
   * direction `VisitorJoinResult.hasAttachmentUploadGrant`'s own remarks commit to.
   */
  onAttachmentUploadGrantChange(listener: (hasAttachmentUploadGrant: boolean) => void): void {
    this.grantListener = listener;
  }

  private emitGrantChange(result: VisitorJoinResult): void {
    this.grantListener?.(result.hasAttachmentUploadGrant ?? false);
  }

  /**
   * `18-12`: this is the "conversation actually starts" moment the backlog item means, not widget
   * mount/page load - `ui/widget.ts`'s own `connect()` (the only caller) already runs this lazily, on
   * first open, not eagerly at mount (that method's own doc comment: "a widget can sit open on a page
   * a visitor never messages through" is exactly the case this avoids). `captureTrafficSource` is read
   * here, once, and travels on `JoinWithTrafficSourceAsync` - a second hub method rather than more
   * parameters on `JoinAsync`, matching the arity-safety split that method's own server-side doc
   * comment (`VisitorHub.cs`) explains. `resumeAfterReconnect` below keeps calling plain `JoinAsync`
   * with no source at all: a resumed connection always has an existing conversation, and a source is
   * captured once, at start, never resent.
   *
   * `23-53`: **never sends the stored `lastKnownSequence` on this call.** It used to - the same
   * cursor `resumeAfterReconnect` below sends to resume a *live* connection that dropped mid-session.
   * The two situations only look alike; they are not the same request. A live reconnect's DOM already
   * holds every message up to that cursor, so a delta of only what is *newer* is exactly right. This
   * method runs once per page load, before this widget has rendered a single bubble - the DOM is
   * always empty here, no matter how full `storage`'s cursor is. Asking the server for "only what
   * changed since sequence N" and getting back nothing, because nothing *has* changed since N, then
   * renders as literally nothing: not stale, not partial, an empty transcript in front of a visitor
   * who wrote real messages into a conversation the operator can still see in full - `23-53`'s own
   * bug report. `storage`'s cursor is a resume-a-live-connection concept, and this is a fresh one, so
   * this call always asks the plain "most recent page" question `JoinCoreAsync` already answers when
   * `lastKnownSequence` is absent (`VisitorHub.cs`) - the identical shape the very first visit already
   * used (nothing stored yet); the returning visit is no longer treated as a different case.
   *
   * `sequenceTracker` is still seeded from the stored cursor, not reset to empty: it is purely a
   * forward-tracking device (`rememberSequence` below only ever raises it), and starting it at the
   * last value this browser is known to have seen is what makes a *live* reconnect later in this same
   * page load ask for the right delta - the field this constructor doc comment describes is unrelated
   * to what gets sent on this call, only to what would be sent on the next reconnect after it.
   *
   * **The shared-device question, answered rather than left implicit** (`23-53`'s own Scope): a
   * second person at the same machine, presenting the first visitor's still-valid token, now reads
   * the first visitor's history as well as continuing it - accepted, on the reasoning `23-53` itself
   * offers as the cheaper honest option, because the token this call presents already lets that
   * second person *write* into the same conversation as the first (`sendMessage` takes no further
   * proof than the same token), so a token that can add to a transcript being able to read it is not
   * a new trust boundary, only a more complete view across an existing one; the alternative -
   * bounding what a rejoined connection can see to messages sent since *this* connection, discarding
   * everything from before it - would hide a visitor's own words from themselves on the device that
   * wrote them, on every single reload, to guard a sharing scenario this widget already could not
   * stop at the write path.
   */
  async start(): Promise<VisitorJoinResult> {
    this.stateListener?.("connecting");
    await this.connection.start();

    const storedConversationId = this.storage.getConversationId();
    const lastKnownSequence =
      storedConversationId !== null ? this.storage.getLastKnownSequence(storedConversationId) : null;
    this.sequenceTracker = new SequenceTracker(lastKnownSequence);

    const source = captureTrafficSource();
    const result = await this.connection.invoke<VisitorJoinResult>(
      "JoinWithTrafficSourceAsync",
      undefined,
      source.referrerHost,
      source.utmSource,
      source.utmMedium,
      source.utmCampaign,
    );

    this.conversationId = result.conversationId;
    this.storage.setConversationId(result.conversationId);
    for (const message of result.history) {
      this.rememberSequence(message);
      this.seenMessageIds.markSeen(message.id);
      // `25-119`: a fresh page load is itself a "reconnecting later" moment for a visitor whose
      // browser never got the chance to ack an operator message before (closed the tab, crashed,
      // lost the socket mid-session) - see `acknowledgeDelivered`'s own doc comment for why acking
      // an already-`DeliveredAt`-set message again here, on every single reload, is accepted rather
      // than guarded against.
      this.acknowledgeDelivered(message);
    }

    this.emitGrantChange(result);
    this.stateListener?.("connected");
    return result;
  }

  /**
   * `NotConnectedError` is safe to retry once the widget observes "connected" again - nothing was
   * sent. `SendOutcomeUnknownError` is not: see that class's doc comment for why this widget does
   * not yet have a safe way to retry that case automatically.
   *
   * `clientMessageId` is required, not optional: `VisitorHub.SendMessageAsync` is a 4-parameter hub
   * method (`5-07`) and this server's SignalR dispatcher does not fill a missing trailing argument
   * from the C# default - found live (`8-02`), omitting it here made every real send fail with a
   * generic "error on the server", never reaching `SendVisitorMessageHandler` at all.
   *
   * `20-07`, corrected: `contentKind`/`content` are how a **structured reply** rides the send path -
   * "reply-by-id, never free text, on any channel including the widget": clicking a primitive's
   * action, or submitting its `form` input, sends the reply through this same function. But
   * `VisitorHub.SendMessageAsync` and `VisitorHub.SendStructuredMessageAsync` (`5-19`) are two
   * separate hub methods, not one four-parameter method that happens to grow two more when a reply
   * is structured - this is the identical arity rule the comment above already states, applied to
   * the case that slipped through when `20-07` landed: every deployed client's send failed at once
   * (invoking `SendMessageAsync` with six arguments against a method declared to take four), found
   * live against the real demo, not in a test, because `20-07`'s own Done-when never got a live
   * click-through. This method now picks the target by whether `contentKind` is set, the same
   * branch `5-19`'s own server-side split already makes structural.
   *
   * `25-31`: `content` is typed `string`, not `unknown` - it rides the wire as `ago-chat`'s
   * `MessagePayload`, which is itself a `string` the server parses as JSON. A caller must
   * `JSON.stringify` its own payload before this point; this method never does it on a caller's
   * behalf, the same "no second, hand-rolled serialization to keep in sync" reasoning
   * `StructuredContentBinder`'s own remarks give for staying shape-only on the server side. Typing
   * this `unknown` let a raw object reach here undetected - SignalR's JSON hub protocol serializes
   * each argument by its own JS shape, so an object landed on the wire as a JSON object where the
   * hub method's parameter is `string?`, a mismatch SignalR rejects before the method is ever
   * invoked (no server log, a generic client-side error) - found live, this exact way.
   *
   * `23-64`/`adr/0148`: `materializeAutoGreeting` picks a *third* target,
   * `VisitorHub.SendMessageWithAutoGreetingAsync` - the identical "a second hub method, never a
   * parameter on `SendMessageAsync`" reasoning this comment already states for structured content,
   * applied to a second new capability. `ui/widget.ts`'s `completeSend` is the one caller that ever
   * passes `true`, and only for the one send it decides is eligible (that method's own remarks).
   * Mutually exclusive with `contentKind` in practice - the first send after an auto-opened panel is
   * always plain typed text - so this parameter is checked first and short-circuits the existing
   * structured-vs-plain branch below rather than adding a third arm to it.
   */
  async sendMessage(
    conversationId: string,
    body: string,
    clientMessageId: string,
    attachmentId?: string,
    contentKind?: string,
    content?: string,
    materializeAutoGreeting?: boolean,
  ): Promise<number> {
    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new NotConnectedError();
    }

    try {
      if (materializeAutoGreeting) {
        return await this.connection.invoke<number>(
          "SendMessageWithAutoGreetingAsync",
          conversationId,
          body,
          attachmentId ?? null,
          clientMessageId,
        );
      }

      return contentKind === undefined
        ? await this.connection.invoke<number>(
            "SendMessageAsync",
            conversationId,
            body,
            attachmentId ?? null,
            clientMessageId,
          )
        : await this.connection.invoke<number>(
            "SendStructuredMessageAsync",
            conversationId,
            body,
            attachmentId ?? null,
            clientMessageId,
            contentKind,
            content ?? null,
            null,
          );
    } catch (error) {
      if (this.connection.state !== signalR.HubConnectionState.Connected) {
        throw new SendOutcomeUnknownError(error);
      }

      throw error;
    }
  }

  async loadOlderHistory(conversationId: string, beforeSequence: number, pageSize: number): Promise<HistoryPage> {
    const page = await this.connection.invoke<HistoryPage>(
      "GetHistoryAsync",
      conversationId,
      beforeSequence,
      pageSize,
    );
    for (const message of page.messages) {
      this.seenMessageIds.markSeen(message.id);
    }

    return page;
  }

  async stop(): Promise<void> {
    await this.connection.stop();
  }

  private async resumeAfterReconnect(): Promise<void> {
    if (this.conversationId === null) {
      this.stateListener?.("connected");
      return;
    }

    const lastKnownSequence = this.sequenceTracker.lastKnownSequence;
    const result = await this.connection.invoke<VisitorJoinResult>("JoinAsync", lastKnownSequence ?? undefined);
    for (const message of result.history) {
      this.handleIncoming(message);
    }

    this.emitGrantChange(result);
    this.stateListener?.("connected");
  }

  private handleIncoming(dto: MessageDto): void {
    this.rememberSequence(dto);
    if (this.seenMessageIds.markSeen(dto.id)) {
      this.messageListener?.(dto);
      // `25-119`: only for a message this connection had not already recorded as seen - the exact
      // gate that already exists to stop a resume delta that overlaps a live push (this file's own
      // `connection.test.ts`, "does not deliver a message twice when the resume delta overlaps a live
      // push") from rendering the same message twice also stops it from acking the same message
      // twice inside one connection's lifetime. This is a courtesy, not a correctness requirement -
      // `acknowledgeDelivered`'s own doc comment covers why a genuine duplicate is harmless anyway.
      this.acknowledgeDelivered(dto);
    }
  }

  /**
   * `25-119`: the widget's own half of the delivered-ack round trip
   * (`docs/backlog/25-119-the-widget-gives-an-operator-no-delivered-signal.md`, "Answered - the
   * design", points 2 and 5). "Delivered" here means this connection's own JS runtime actually
   * received the push, not merely that the server attempted one - a stronger, more honest signal
   * than `ChannelDelivery.Delivered` ever was for a Telegram/MAX conversation.
   *
   * Scoped to `authorKind === "Operator"` only, the *exact* gate `ago-console`'s `Thread.tsx` already
   * uses for the channel-kind badge (`item.message.authorKind === "Operator"`) - not `"AutoGreeting"`,
   * even though that kind renders with the operator's own bubble styling (`protocol/types.ts`'s own
   * remarks on why): a visitor's own echoed-back message, a `"System"` message, and an
   * `"AutoGreeting"` message all have no "did the operator see it" concept this item was asked to
   * build, and console's own precedent for what counts as "operator-authored" for this exact badge
   * is the one this file matches rather than inventing its own.
   *
   * Fire-and-forget through `guardAsync` (`errors.ts`) - the one convention this codebase already has
   * for a connection-boundary call this widget must not let throw into `@microsoft/signalr`'s own
   * event loop (`errors.ts`'s own doc comment names "connection callbacks" as one of the boundaries
   * `guardAsync`/`guardSync` exist for). This file had no *other* fire-and-forget hub invocation to
   * match before this one - every existing `.invoke(...)` call here (`start`, `sendMessage`,
   * `loadOlderHistory`, `resumeAfterReconnect`) is awaited and lets its rejection propagate to a
   * caller that decides what to do about it - so `guardAsync`'s swallow-and-log behaviour (last
   * resort: `console.error`, never a silent catch) is the closest existing convention for a call this
   * method deliberately does not want to block message rendering on, or retry itself: no retry on
   * failure, and no local idempotency ledger, because `adr/0020`'s "at-least-once, redelivery is
   * harmless" already covers a duplicate or a lost ack the same way it covers every other consumer in
   * this system - the backend's own handler treats a repeat ack against an already-`DeliveredAt`-set
   * message as a no-op, so sending one more than strictly necessary costs nothing.
   */
  private acknowledgeDelivered(message: MessageDto): void {
    const conversationId = this.conversationId;
    if (message.authorKind !== "Operator" || conversationId === null) {
      return;
    }

    guardAsync(() => this.connection.invoke<void>("AcknowledgeDeliveredAsync", conversationId, message.id));
  }

  private rememberSequence(message: MessageDto): void {
    if (this.conversationId !== null && this.sequenceTracker.observe(message.sequence)) {
      this.storage.setLastKnownSequence(this.conversationId, message.sequence);
    }
  }
}
